import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { flexRender, Table as ReactTableInstance } from '@tanstack/react-table';
import classNames from 'classnames';
import { Fragment } from 'react/jsx-runtime';
import { z } from 'zod';
import Button from '../button';

type TableProps<T> = {
  table: ReactTableInstance<T>;
  rowOnClick?: (row: T) => void;
  dataTestId?: string;
  /**
   * If the table contains a large number of elements, it is possible to enable
   * pagination via this prop. The actual pagination logic happens in the parent
   * component. Therefore, all sub-props have to be set.
   */
  pagination?: {
    numberOfPages?: number;
    currentPage: number;
    onNextPage: () => void;
    onPreviousPage: () => void;
    isLoading: boolean;
  };
};

// We make the column meta into Zod Schema so we can safely work with the column meta data
// This also done to avoid module level alteration, check:
// https://tanstack.com/table/v8/docs/api/core/column-def#meta
export const TableColumnMetaSchema = z
  .object({
    isHeaderCentered: z.boolean().optional(),
    isCellCentered: z.boolean().optional(),
    hasLeftBorder: z.boolean().optional(),
    hasRightBorder: z.boolean().optional()
  })
  .optional();

/**
 * TODO: We should re-architect the table component to ensure scalability.
 *
 * We should think about not using tanstack table as we are doing all sorting,
 * filtering, and pagination on the server since, in most cases, we cannot
 * load all data at once.
 *
 * A suggested approach is to break the table up into individual components for
 *
 * - Table
 * - Tr
 * - Td
 * - Pagination
 *
 * This will allow for a more manageable implementation of "Link" logic as well.
 *
 * @deprecated: Use TableV2 instead.
 */
const Table = <T,>({
  table,
  rowOnClick,
  dataTestId,
  pagination
}: TableProps<T>) => {
  const numberOfStickyHeaders =
    table.getHeaderGroups()?.[0]?.headers.filter((h) => h.column.getIsPinned())
      .length ?? 0;

  return (
    // Either Utilize Fragment or explicitly set the overflow rule
    // Sticky requires all parent component to have right overflow context
    <Fragment>
      <div
        className="rounded-radius-02 overflow-hidden shadow-shadow-01"
        data-test-id={dataTestId}
      >
        <div className="relative overflow-x-auto flex flex-gow">
          <table
            className="table-fixed bg-white min-w-full overflow-y-scroll overflow-x-auto body-02 border-separate border-spacing-0"
            {...{
              style: {
                width: table.getCenterTotalSize()
              }
            }}
            data-test-id={`${dataTestId}-table`}
          >
            <thead className="rounded-radius-02 border-b">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-b border-secondary"
                  data-test-id={`${dataTestId}-header-tr`}
                >
                  {headerGroup.headers.map((header) => {
                    const meta = TableColumnMetaSchema.parse(
                      header.column.columnDef.meta
                    );
                    const pinnedColumns = table
                      .getVisibleLeafColumns()
                      .filter((col) => col.getIsPinned());
                    const cumulativeWidth = pinnedColumns
                      .slice(
                        0,
                        pinnedColumns.findIndex(
                          (col) => col.id === header.column.id
                        )
                      )
                      .reduce((sum, col) => sum + col.getSize(), 0);

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={classNames(
                          'bg-white cursor-pointer border-b border-secondary',
                          {
                            'sticky left-0 z-sticky':
                              header.column.getIsPinned(),
                            'border-r border-secondary':
                              numberOfStickyHeaders - 1 === header.index ||
                              meta?.hasRightBorder,
                            'border-l border-secondary': meta?.hasLeftBorder
                          }
                        )}
                        style={{
                          left: header.column.getIsPinned()
                            ? `${cumulativeWidth}px`
                            : undefined,
                          width: header.column.getSize()
                        }}
                        data-test-id={`${dataTestId}-th-${header.id}`}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: classNames(
                                'relative flex w-full h-full px-spacing-04 items-center text-start text-primary py-spacing-03 text-heading-04 font-semibold',
                                {
                                  'cursor-pointer select-none':
                                    header.column.getCanSort(),
                                  'flex flex-row items-center justify-center w-full':
                                    meta?.isHeaderCentered
                                }
                              ),
                              onClick: header.column.getToggleSortingHandler(),
                              colSpan: header.colSpan
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}

                            {{
                              asc: (
                                <div className="absolute flex top-0 right-4 bottom-0 items-center">
                                  ↑
                                </div>
                              ),
                              desc: (
                                <div className="absolute flex top-0 right-4 bottom-0 items-center">
                                  ↓
                                </div>
                              )
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            {/* TBODY */}
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={classNames('hover:bg-tertiary', {
                    'bg-secondary': row.index % 2 === 0,
                    'bg-white': row.index % 2 === 1,
                    'cursor-pointer': Boolean(rowOnClick)
                  })}
                  onClick={() => {
                    if (rowOnClick) rowOnClick(row.original);
                  }}
                  data-test-id={`${dataTestId}-row-${row.id}`}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const meta = TableColumnMetaSchema.parse(
                      cell.column.columnDef.meta
                    );

                    const pinnedColumns = table
                      .getVisibleLeafColumns()
                      .filter((col) => col.getIsPinned());
                    const cumulativeWidth = pinnedColumns
                      .slice(
                        0,
                        pinnedColumns.findIndex(
                          (col) => col.id === cell.column.id
                        )
                      )
                      .reduce((sum, col) => sum + col.getSize(), 0);
                    return (
                      <td
                        key={cell.id}
                        className={classNames({
                          'sticky z-sticky': cell.column.getIsPinned(),

                          'bg-secondary': row.index % 2 === 0,
                          'bg-white': row.index % 2 === 1,

                          'border-r border-secondary':
                            numberOfStickyHeaders - 1 === index ||
                            meta?.hasRightBorder,
                          'border-l border-secondary': meta?.hasLeftBorder
                        })}
                        style={{
                          left: cell.column.getIsPinned()
                            ? `${cumulativeWidth}px`
                            : undefined,
                          width: cell.column.getSize()
                        }}
                        data-test-id={`${dataTestId}-td-${cell.column.id}-${row.id}`}
                      >
                        <div
                          className={classNames(
                            'flex items-center px-spacing-04 text-primary text-body-2 w-full h-[48px]',
                            {
                              'flex flex-row items-center justify-center w-full':
                                meta?.isHeaderCentered
                            }
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination && (
        <div className="flex flex-row items-center justify-end space-x-spacing-02 mt-spacing-04 text-secondary">
          <Button
            variant="GHOST"
            size="SMALL"
            iconLeft={<ChevronLeft />}
            disabled={pagination.isLoading || pagination.currentPage < 1}
            onClick={pagination.onPreviousPage}
          />

          <p>
            {/* Current page is 0-indexed */}
            <span>{pagination.currentPage + 1}</span> of{' '}
            <span>{pagination.numberOfPages ?? 'many'}</span>
          </p>

          <Button
            variant="GHOST"
            size="SMALL"
            iconLeft={<ChevronRight />}
            disabled={
              pagination.isLoading ||
              pagination.currentPage + 1 === pagination.numberOfPages
            }
            onClick={pagination.onNextPage}
          />
        </div>
      )}
    </Fragment>
  );
};

export default Table;
